import { useEffect, useState } from 'react';

import { create } from 'zustand';

import {
  type AnyModalStore,
  isAnyModalOpen,
  registerModalOccupant,
  subscribeToSettledModalClose,
} from '@/shared/lib/modal';

type TeamspaceWelcomeModalState = {
  isNewUser: boolean;
  showTooltip: boolean;
  spaceId: null | string;
  reset: () => void;
  setSpaceId: (value: null | string) => void;
};

export const useTeamspaceWelcomeModal = create<TeamspaceWelcomeModalState>((set) => ({
  isNewUser: false,
  showTooltip: false,
  spaceId: null,
  reset: () => set({ isNewUser: true, showTooltip: true, spaceId: null }),
  setSpaceId: (value) => set({ isNewUser: true, spaceId: value }),
}));

export const teamspaceWelcomeModalOccupant: AnyModalStore = {
  subscribe: (listener) =>
    useTeamspaceWelcomeModal.subscribe((state, previous) =>
      listener({ isOpen: state.spaceId !== null }, { isOpen: previous.spaceId !== null }),
    ),
  getState: () => {
    const { spaceId, reset } = useTeamspaceWelcomeModal.getState();

    return { close: reset, isOpen: spaceId !== null };
  },
};

registerModalOccupant(teamspaceWelcomeModalOccupant);

export const useIsTeamspaceWelcomeVisible = () => {
  const hasWelcome = useTeamspaceWelcomeModal((state) => state.spaceId !== null);
  const [isVisible, setIsVisible] = useState(false);
  const [modalCloseCount, setModalCloseCount] = useState(0);

  useEffect(() => {
    if (!hasWelcome) {
      setIsVisible(false);
      return;
    }

    if (isVisible) return;

    if (isAnyModalOpen(teamspaceWelcomeModalOccupant)) {
      return subscribeToSettledModalClose(() => setModalCloseCount((count) => count + 1));
    }

    setIsVisible(true);
  }, [hasWelcome, isVisible, modalCloseCount]);

  return hasWelcome && isVisible;
};
